/**
 * calibrationBiasReducers.ts
 *
 * Pure analytics that surface the two bias-reduction mechanisms
 * mandated by the framework Calibration Session checklist:
 *
 *   1. "Review all 5s & 1s in the ratings — to ensure 'exceptional' is
 *      truly exceptional, not just 'good' and ensure 'unsatisfactory'
 *      is properly identified and documented."
 *
 *   2. "Identify lenient/strict managers — to spot managers who
 *      consistently rate higher or lower than peers."
 *
 * Both functions are framework-compliant pure functions with vitest
 * coverage. They take an array of EPRSubmissions and return analytics
 * objects ready for direct rendering.
 */

import type { EPRItem, EPRSubmission } from '../types/eprFramework';

// ─── 1. Extreme score review (5s & 1s) ────────────────────────────

export interface ExtremeScoreRow {
  submissionId: string;
  employeeId:   string;
  supervisorId: string;
  itemId:       string;
  itemName:     string;
  component:    EPRItem['component'];
  managerScore: 1 | 2 | 5;   // 5 = exceptional, ≤2 = needs documentation
  employeeSelfScore?: number;
  gap?: number;              // |self - manager|
  notesEvidence?: string;
  hasEvidence:  boolean;
}

/**
 * All item-level manager scores of 5 OR ≤ 2 in the cycle. The framework
 * highlights these as the rows that drive calibration adjustments — a
 * "5" must be exceptional (not just good), a "1-2" must be documented.
 *
 * Sorting puts items with missing evidence first (HR needs to chase the
 * manager for justification), then by score (5s grouped, then 2s, then 1s).
 */
export function findExtremeScores(submissions: EPRSubmission[]): ExtremeScoreRow[] {
  const out: ExtremeScoreRow[] = [];

  for (const sub of submissions) {
    const all: EPRItem[] = [
      ...sub.okrItems,
      ...sub.kpiItems,
      ...sub.coreItems,
      ...sub.leadershipItems,
    ];
    for (const item of all) {
      const ms = item.managerScore;
      if (ms === 5 || ms === 2 || ms === 1) {
        const gap = item.employeeSelfScore != null
          ? Math.abs(item.employeeSelfScore - ms)
          : undefined;
        out.push({
          submissionId:      sub.id,
          employeeId:        sub.employeeId,
          supervisorId:      sub.supervisorId,
          itemId:            item.itemId,
          itemName:          item.itemName,
          component:         item.component,
          managerScore:      ms,
          employeeSelfScore: item.employeeSelfScore,
          gap,
          notesEvidence:     item.notesEvidence,
          hasEvidence:       !!(item.notesEvidence?.trim()),
        });
      }
    }
  }

  // Sort: no-evidence first (urgency), then by score 5→1 (descending impact)
  out.sort((a, b) => {
    if (a.hasEvidence !== b.hasEvidence) return a.hasEvidence ? 1 : -1;
    return b.managerScore - a.managerScore;
  });

  return out;
}

// ─── 2. Lenient / strict manager detection ────────────────────────

export type BiasFlag = 'lenient' | 'strict' | 'neutral';

export interface ManagerRatingStat {
  supervisorId: string;
  reviewees:    number;
  itemsScored:  number;
  avgGiven:     number;     // mean of all manager-given item scores
  orgAvg:       number;     // mean across all managers (same metric)
  deviation:    number;     // avgGiven - orgAvg
  bias:         BiasFlag;
}

export interface DetectBiasOptions {
  /** Deviation threshold for the lenient/strict flag. Default ±0.5. */
  threshold?: number;
  /** Minimum items scored for the analysis to be meaningful. Default 3. */
  minItems?:  number;
}

/**
 * Per-manager average + flag relative to the org average. The framework
 * recommends spotting managers who "consistently rate higher or lower
 * than peers" — by default any manager whose avgGiven is ≥ 0.5 above
 * (lenient) or ≤ 0.5 below (strict) the org average is flagged.
 *
 * Returns one row per supervisor, sorted by absolute deviation (most
 * biased first) so HR sees the outliers at the top.
 */
export function detectManagerBias(
  submissions: EPRSubmission[],
  opts: DetectBiasOptions = {},
): ManagerRatingStat[] {
  const threshold = opts.threshold ?? 0.5;
  const minItems  = opts.minItems ?? 3;

  // 1. Collect all manager scores per supervisor
  const perMgr = new Map<string, { scores: number[]; reviewees: Set<string> }>();
  for (const sub of submissions) {
    const items: EPRItem[] = [
      ...sub.okrItems, ...sub.kpiItems, ...sub.coreItems, ...sub.leadershipItems,
    ];
    const scores = items
      .map(i => i.managerScore)
      .filter((s): s is 1 | 2 | 3 | 4 | 5 => typeof s === 'number');
    if (scores.length === 0) continue;
    if (!perMgr.has(sub.supervisorId)) {
      perMgr.set(sub.supervisorId, { scores: [], reviewees: new Set() });
    }
    const bucket = perMgr.get(sub.supervisorId)!;
    bucket.scores.push(...scores);
    bucket.reviewees.add(sub.employeeId);
  }

  // 2. Org average across all manager-given scores (every score weighed equally)
  let allCount = 0;
  let allSum = 0;
  for (const b of perMgr.values()) {
    allCount += b.scores.length;
    allSum += b.scores.reduce((a, s) => a + s, 0);
  }
  const orgAvg = allCount > 0 ? allSum / allCount : 0;

  // 3. Per-manager stat with bias flag
  const out: ManagerRatingStat[] = [];
  for (const [supervisorId, b] of perMgr.entries()) {
    if (b.scores.length < minItems) continue;
    const avgGiven = b.scores.reduce((a, s) => a + s, 0) / b.scores.length;
    const deviation = avgGiven - orgAvg;
    let bias: BiasFlag = 'neutral';
    if (deviation >= threshold) bias = 'lenient';
    else if (deviation <= -threshold) bias = 'strict';

    out.push({
      supervisorId,
      reviewees:   b.reviewees.size,
      itemsScored: b.scores.length,
      avgGiven,
      orgAvg,
      deviation,
      bias,
    });
  }

  // Sort: biggest absolute deviation first
  out.sort((a, b) => Math.abs(b.deviation) - Math.abs(a.deviation));
  return out;
}
